import { isPlatformBrowser } from '@angular/common';
import {
    AfterViewInit,
    Directive,
    ElementRef,
    inject,
    Inject,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Optional,
    PLATFORM_ID,
    Renderer2,
    RendererStyleFlags2,
    SimpleChanges
} from '@angular/core';
import { App } from '@df/core/app';
import { OnInteractionService } from '@df/ui/common/on-interaction.service';
import { GSAP_SCROLLTRIGGER_OPTIONS } from '@df/ui/display/gsap/gsap.types';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Subject, Subscription } from 'rxjs';

@Directive({
    selector: '[gsap]',
    standalone: true
})
export class GSAPDirective implements OnInit, AfterViewInit, OnDestroy, OnChanges {
    @Input()
    gsap?: Partial<gsap.TweenVars> | Partial<gsap.TweenVars>[];

    @Input()
    gsapOff?: boolean;

    @Input()
    scrollTrigger?: Partial<gsap.plugins.ScrollTriggerInstanceVars>;

    @Input()
    progressTarget?: HTMLElement;

    @Input()
    gsapUpdate = new Subject<void>();

    private _uid = `gsap-${crypto.randomUUID}`;
    private timeline?: gsap.core.Timeline;

    private initiated = false;
    private initialSubscription?: Subscription;
    private subscriptions = new Subscription();

    private readonly isPlatformBrowser = isPlatformBrowser(inject(PLATFORM_ID));

    constructor(
        protected renderer2: Renderer2,
        protected elementRef: ElementRef,
        protected ngZone: NgZone,
        protected app: App,
        private onInteractionService: OnInteractionService,
        @Optional() @Inject(GSAP_SCROLLTRIGGER_OPTIONS) protected scrollTriggerOptions: Partial<gsap.plugins.ScrollTriggerInstanceVars>
    ) {}

    get disabled(): boolean {
        return !!this.gsapOff;
    }

    ngOnInit(): void {
        if (!this.disabled) {
            this.registerGsapAnimation();
        }
    }

    ngAfterViewInit(): void {
        this.initialSubscription = this.onInteractionService.anyInteraction.subscribe(value => {
            if (value) {
                this.initialSubscription?.unsubscribe();
                this.initiated = true;
                this.update();
            }
        });

        this.subscriptions.add(this.gsapUpdate.subscribe(() => this.update()));
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes) {
            this.update();
        }
    }

    ngOnDestroy(): void {
        this.kill();

        this.subscriptions.unsubscribe();
    }

    protected update(): void {
        if (!this.initiated) {
            return;
        }
        this.kill();

        if (this.app.isPlatformBrowser && !this.disabled) {
            this.ngZone.runOutsideAngular(() => {
                const _targetElement = this.elementRef.nativeElement;
                // default global options
                const _scrollTriggerOptions = this.scrollTriggerOptions || {};
                // local options
                const _scrollTrigger = this.scrollTrigger || {};

                const scrollTrigger = {
                    id: this._uid,
                    scrub: true,
                    ..._scrollTriggerOptions,
                    trigger: _targetElement,
                    onUpdate: ({ progress }) => {
                        this.renderer2.setStyle(
                            this.progressTarget || this.elementRef.nativeElement,
                            '--gsap-progress',
                            `${progress || 0}`,
                            RendererStyleFlags2.DashCase
                        );
                    },
                    ..._scrollTrigger
                };

                this.timeline = gsap.timeline({
                    scrollTrigger
                });

                if (this.gsap) {
                    const tweens: Partial<gsap.TweenVars>[] = !Array.isArray(this.gsap) ? [this.gsap] : this.gsap;

                    tweens.forEach(options => {
                        let fromVars: Partial<gsap.TweenVars> | undefined = undefined;
                        let toVars: Partial<gsap.TweenVars> = {
                            ease: 'none'
                        };

                        if (options['fromVars']) {
                            fromVars = { ...options['fromVars'] };
                        }

                        if (options['toVars']) {
                            toVars = { ...toVars, ...options['toVars'] };
                        } else if (!options['fromVars']) {
                            toVars = { ...toVars, ...options };
                        }

                        const method = fromVars && toVars ? 'fromTo' : fromVars ? 'from' : 'to';
                        const tween = gsap[method](_targetElement, fromVars || toVars, toVars);

                        this.timeline?.add(tween);
                    });
                }
            });
        } else {
            this.renderer2.setStyle(
                this.progressTarget || this.elementRef.nativeElement,
                '--gsap-progress',
                '0',
                RendererStyleFlags2.DashCase
            );
        }
    }

    protected kill(): void {
        if (this.timeline) {
            this.timeline.progress(0);
            this.timeline.clear(true);
            this.timeline.kill();
            this.timeline = undefined;

            ScrollTrigger.getById(this._uid)?.kill();

            gsap.set(this.elementRef.nativeElement, { clearProps: true });
        }
    }

    protected registerGsapAnimation() {
        if (this.isPlatformBrowser) {
            gsap.registerPlugin(ScrollTrigger);
        }
    }
}
